import { useCallback, useEffect, useRef, useState } from 'react'

import AnswersApiService from '@/apis/answersApiService'
import type { Message } from '@/types/chat'

const EMPTY_MESSAGES: Message[] = []

interface UseChatOptions {
  initialMessages?: Message[]
  conversationId: string | null
  initialActiveUserMessageId?: string | null
  onConversationCreated?: (id: string) => void
}

export default function useChat({
  initialMessages = EMPTY_MESSAGES,
  conversationId,
  onConversationCreated,
}: UseChatOptions) {
  const [messages, setMessages] = useState<Message[]>(initialMessages)
  const [streamingContent, setStreamingContent] = useState('')
  const [isStreaming, setIsStreaming] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const abortRef = useRef<AbortController | null>(null)
  const conversationIdRef = useRef(conversationId)

  useEffect(() => {
    conversationIdRef.current = conversationId
  }, [conversationId])

  useEffect(() => {
    setMessages(initialMessages)
  }, [initialMessages])

  const sendMessage = useCallback(
    async (question: string) => {
      if (isStreaming) return

      setError(null)
      setIsStreaming(true)
      setStreamingContent('')

      const userMessage: Message = {
        id: `temp-${Date.now()}`,
        role: 'user',
        content: question,
        citations: null,
        audioUrl: null,
        createdAt: new Date().toISOString(),
      }
      setMessages((prev) => [...prev, userMessage])

      const controller = new AbortController()
      abortRef.current = controller

      let accumulated = ''

      try {
        await AnswersApiService.stream(question, conversationIdRef.current, controller.signal, {
          onConversationId: (id) => {
            conversationIdRef.current = id
            onConversationCreated?.(id)
          },
          onChunk: (text) => {
            accumulated += text
            setStreamingContent(accumulated)
          },
          onDone: () => {
            const assistantMessage: Message = {
              id: `temp-${Date.now()}-assistant`,
              role: 'assistant',
              content: accumulated,
              citations: null,
              audioUrl: null,
              createdAt: new Date().toISOString(),
            }
            setMessages((prev) => [...prev, assistantMessage])
            setStreamingContent('')
            setIsStreaming(false)
          },
          onError: (err) => {
            setError(err.message)
            setIsStreaming(false)
            setStreamingContent('')
          },
        })
      } catch (err) {
        if ((err as Error).name !== 'AbortError') {
          setError((err as Error).message)
        }
        setIsStreaming(false)
        setStreamingContent('')
      }
    },
    [isStreaming, onConversationCreated],
  )

  const stop = useCallback(() => {
    abortRef.current?.abort()
    if (streamingContent) {
      const assistantMessage: Message = {
        id: `temp-${Date.now()}-stopped`,
        role: 'assistant',
        content: streamingContent,
        citations: null,
        audioUrl: null,
        createdAt: new Date().toISOString(),
      }
      setMessages((prev) => [...prev, assistantMessage])
    }
    setStreamingContent('')
    setIsStreaming(false)
  }, [streamingContent])

  useEffect(
    () => () => {
      abortRef.current?.abort()
    },
    [],
  )

  return { messages, streamingContent, isStreaming, error, sendMessage, stop }
}
